import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { TrendingDown, Calculator, PiggyBank, Target, Home, AlertTriangle, Info, Unlock } from 'lucide-react';
import { mockCalculateSWP } from '../mock/swpMock';
import LeadCaptureModal from './LeadCaptureModal';

const SWPPlanner = () => {
  const navigate = useNavigate();

  // Set page title
  React.useEffect(() => {
    document.title = 'Wealth Preservation Planner - AUMONLINE';
  }, []);

  const [calculationMode, setCalculationMode] = useState('corpus-to-income'); // 'corpus-to-income' or 'income-to-corpus'
  const [formData, setFormData] = useState({
    corpusAmount: '',
    monthlyIncome: '',
    swpPercentage: '4'
  });

  const [results, setResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculate = () => {
    // Validate inputs based on calculation mode
    if (calculationMode === 'corpus-to-income') {
      if (!formData.corpusAmount || !formData.swpPercentage) {
        alert('Please fill in corpus amount and SWP percentage');
        return;
      }
      if (parseFloat(formData.corpusAmount) <= 0) {
        alert('Please enter a valid corpus amount');
        return;
      }
    } else {
      if (!formData.monthlyIncome || !formData.swpPercentage) {
        alert('Please fill in monthly income requirement and SWP percentage');
        return;
      }
      if (parseFloat(formData.monthlyIncome) <= 0) {
        alert('Please enter a valid monthly income requirement');
        return;
      }
    }

    if (parseFloat(formData.swpPercentage) <= 0) {
      alert('Please enter a valid SWP percentage');
      return;
    }

    const calculatedResults = mockCalculateSWP(formData, calculationMode);
    setResults(calculatedResults);
    setShowPreview(true);
    setShowFullResults(false);
  };

  const handleReset = () => {
    setFormData({
      corpusAmount: '',
      monthlyIncome: '',
      swpPercentage: '4'
    });
    setResults(null);
    setShowPreview(false);
    setShowFullResults(false);
  };

  const handleUnlockFullRoadmap = () => {
    setIsLeadModalOpen(true);
  };

  const handleLeadSubmit = async (leadData) => {
    setIsSubmittingLead(true);
    
    try {
      const webhookPayload = {
        lead_source: 'AUMOnline Planner',
        tool_used: 'SWP Planner',
        user_name: leadData.name,
        user_phone: leadData.phone,
        key_metrics: {
          target_corpus: calculationMode === 'corpus-to-income' ? results?.corpusAmount : results?.corpusRequired,
          monthly_investment_needed: calculationMode === 'corpus-to-income' ? results?.monthlySwp : results?.monthlyIncome
        }
      };

      const response = await fetch('https://n8n.srv1138087.hstgr.cloud/webhook/planner-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (response.ok) {
        setIsLeadModalOpen(false);
        setShowFullResults(true);
      } else {
        let errorText = 'Unknown error';
        try {
          errorText = await response.text();
        } catch (e) {
          console.error('Could not read error response:', e);
        }
        throw new Error(`Failed to submit: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`There was an error submitting your details: ${errorMessage}. Please try again.`);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const getSwpWarning = () => {
    const percentage = parseFloat(formData.swpPercentage);
    if (percentage > 6) {
      return {
        type: 'destructive',
        message: 'Warning: SWP above 6% may deplete your corpus during market downturns!'
      };
    } else if (percentage >= 4 && percentage <= 6) {
      return {
        type: 'default',
        message: 'Ideal range: 4% to 6% is considered safe for long-term wealth preservation.'
      };
    } else if (percentage < 4 && percentage > 0) {
      return {
        type: 'default',
        message: 'Conservative approach: Below 4% ensures maximum corpus preservation.'
      };
    }
    return null;
  };

  const warning = getSwpWarning();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex-1 flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-20 md:h-24 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
              <a href="https://www.njwealth.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/0iyf1b7c_image.png" 
                  alt="NJ WEALTH Logo" 
                  className="h-14 md:h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            SWP PLANNER
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Calculate your optimal Systematic Withdrawal Plan to generate regular income while preserving your investment corpus
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-8xl mx-auto">
          {/* Input Section */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white">
              <CardTitle className="text-2xl md:text-3xl text-center flex items-center justify-center gap-3">
                <TrendingDown className="w-8 h-8" />
                Plan Your SWP
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                {/* Calculation Mode Toggle */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-gray-700">
                    Choose Calculation Method:
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button
                      variant={calculationMode === 'corpus-to-income' ? 'default' : 'outline'}
                      onClick={() => {
                        setCalculationMode('corpus-to-income');
                        setResults(null);
                      }}
                      className={`p-4 text-left h-auto ${
                        calculationMode === 'corpus-to-income' 
                          ? 'bg-orange-600 text-white hover:bg-orange-700' 
                          : 'border-2 border-orange-600 text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      <div>
                        <div className="font-semibold mb-1">Corpus → Income</div>
                        <div className="text-sm opacity-90">I have corpus, calculate monthly income</div>
                      </div>
                    </Button>
                    
                    <Button
                      variant={calculationMode === 'income-to-corpus' ? 'default' : 'outline'}
                      onClick={() => {
                        setCalculationMode('income-to-corpus');
                        setResults(null);
                      }}
                      className={`p-4 text-left h-auto ${
                        calculationMode === 'income-to-corpus' 
                          ? 'bg-orange-600 text-white hover:bg-orange-700' 
                          : 'border-2 border-orange-600 text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      <div>
                        <div className="font-semibold mb-1">Income → Corpus</div>
                        <div className="text-sm opacity-90">I need monthly income, calculate required corpus</div>
                      </div>
                    </Button>
                  </div>
                </div>

                {calculationMode === 'corpus-to-income' ? (
                  /* Corpus to Income Mode */
                  <div className="space-y-6">
                    {/* Corpus Amount Input */}
                    <div className="space-y-2">
                      <Label htmlFor="corpusAmount" className="text-lg font-semibold text-gray-700">
                        How much money do you have to start SWP?
                      </Label>
                      <Input
                        id="corpusAmount"
                        name="corpusAmount"
                        type="number"
                        value={formData.corpusAmount}
                        onChange={handleInputChange}
                        placeholder="₹ Enter your investment corpus"
                        className="text-lg p-4 border-2 border-gray-300 focus:border-orange-600"
                      />
                      <p className="text-sm text-gray-500">
                        Enter the total amount from which you want to withdraw systematically
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Income to Corpus Mode */
                  <div className="space-y-6">
                    {/* Monthly Income Requirement Input */}
                    <div className="space-y-2">
                      <Label htmlFor="monthlyIncome" className="text-lg font-semibold text-gray-700">
                        What monthly income do you need from SWP?
                      </Label>
                      <Input
                        id="monthlyIncome"
                        name="monthlyIncome"
                        type="number"
                        value={formData.monthlyIncome}
                        onChange={handleInputChange}
                        placeholder="₹ Enter required monthly income"
                        className="text-lg p-4 border-2 border-gray-300 focus:border-orange-600"
                      />
                      <p className="text-sm text-gray-500">
                        Enter the monthly income you need from your investments
                      </p>
                    </div>
                  </div>
                )}

                {/* SWP Percentage Input */}
                <div className="space-y-2">
                  <Label htmlFor="swpPercentage" className="text-lg font-semibold text-gray-700">
                    SWP Percentage (% per annum)
                  </Label>
                  <Input
                    id="swpPercentage"
                    name="swpPercentage"
                    type="number"
                    step="0.1"
                    value={formData.swpPercentage}
                    onChange={handleInputChange}
                    placeholder="4"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-orange-600"
                  />
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700 mb-2">
                      <Info className="w-4 h-4" />
                      <span className="font-semibold">Recommended: 4%</span>
                    </div>
                    <p className="text-sm text-blue-600">
                      4% to 6% is ideal. Anything more than 6% may deplete the corpus during market downturns.
                    </p>
                  </div>
                </div>

                {/* Warning Alert */}
                {warning && formData.swpPercentage && (
                  <Alert className={`${warning.type === 'destructive' ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}`}>
                    <AlertTriangle className={`h-4 w-4 ${warning.type === 'destructive' ? 'text-red-500' : 'text-green-600'}`} />
                    <AlertDescription className={`${warning.type === 'destructive' ? 'text-red-700' : 'text-green-700'}`}>
                      {warning.message}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Information Box */}
                <div className="bg-gradient-to-r from-orange-100 to-yellow-100 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-3">What is SWP?</h4>
                  <ul className="text-sm space-y-2 text-gray-700">
                    <li>• <strong>Systematic Withdrawal Plan</strong> allows regular income from your investments</li>
                    <li>• You withdraw a fixed percentage annually while keeping the corpus invested</li>
                    <li>• Remaining amount continues to grow with market returns</li>
                    <li>• Ideal for retirees seeking regular income without depleting capital</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="text-center space-y-4">
                  <Button
                    onClick={handleCalculate}
                    className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    <Calculator className="w-6 h-6 mr-3" />
                    CALCULATE SWP
                  </Button>

                  {results && (
                    <Button
                      onClick={handleReset}
                      className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      RESET
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          {results && showPreview && (
            <div className="space-y-6">
              <Card className="shadow-xl border-0">
                <CardHeader className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white">
                  <CardTitle className="text-2xl md:text-3xl text-center">
                    Wealth Preservation Plan Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {calculationMode === 'corpus-to-income' ? (
                      <div className="bg-gradient-to-br from-green-100 to-green-50 p-6 rounded-xl border-l-4 border-green-600">
                        <div className="flex items-center gap-3 mb-3">
                          <TrendingDown className="w-8 h-8 text-green-600" />
                          <h3 className="text-xl font-bold text-gray-800">Monthly SWP Amount</h3>
                        </div>
                        <p className="text-4xl font-bold text-green-600">
                          ₹{results?.monthlySwp?.toLocaleString('en-IN')}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Regular monthly income from your investments
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-6 rounded-xl border-l-4 border-orange-600">
                        <div className="flex items-center gap-3 mb-3">
                          <PiggyBank className="w-8 h-8 text-orange-600" />
                          <h3 className="text-xl font-bold text-gray-800">Total Corpus Required</h3>
                        </div>
                        <p className="text-4xl font-bold text-orange-600">
                          ₹{results?.corpusRequired?.toLocaleString('en-IN')}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Investment needed at {results?.swpPercentage}% SWP rate
                        </p>
                      </div>
                    )}

                    {!showFullResults && (
                      <div className="text-center space-y-4">
                        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                          <p className="text-lg text-gray-700 mb-4">
                            🔒 Want to see the complete breakdown including:
                          </p>
                          <ul className="text-left text-gray-600 space-y-2 mb-4 max-w-md mx-auto">
                            <li>• Detailed corpus and income breakdown</li>
                            <li>• Yearly and monthly withdrawal amounts</li>
                            <li>• Risk assessment and strategy summary</li>
                            <li>• Tax-efficient withdrawal recommendations</li>
                          </ul>
                        </div>
                        <Button
                          onClick={handleUnlockFullRoadmap}
                          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                          <Unlock className="w-6 h-6 mr-3" />
                          Unlock Full Roadmap
                        </Button>
                      </div>
                    )}

                    {showFullResults && (
                      <>
                        {calculationMode === 'corpus-to-income' ? (
                          /* Standard Results */
                          <>
                            <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-6 rounded-xl border-l-4 border-orange-600">
                              <div className="flex items-center gap-3 mb-3">
                                <PiggyBank className="w-8 h-8 text-orange-600" />
                                <h3 className="text-xl font-bold text-gray-800">Investment Corpus</h3>
                              </div>
                              <p className="text-3xl font-bold text-orange-600">
                                ₹{results?.corpusAmount?.toLocaleString('en-IN')}
                              </p>
                            </div>

                            <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-6 rounded-xl border-l-4 border-yellow-600">
                              <div className="flex items-center gap-3 mb-3">
                                <Target className="w-8 h-8 text-yellow-600" />
                                <h3 className="text-xl font-bold text-gray-800">Yearly SWP Amount</h3>
                              </div>
                              <p className="text-3xl font-bold text-yellow-600">
                                ₹{results?.yearlySwp?.toLocaleString('en-IN')}
                              </p>
                              <p className="text-sm text-gray-600 mt-2">
                                {results?.swpPercentage}% of your corpus per year
                              </p>
                            </div>
                          </>
                        ) : (
                          /* Reverse Results */
                          <>
                            <div className="bg-gradient-to-br from-green-100 to-green-50 p-6 rounded-xl border-l-4 border-green-600">
                              <div className="flex items-center gap-3 mb-3">
                                <Target className="w-8 h-8 text-green-600" />
                                <h3 className="text-xl font-bold text-gray-800">Required Monthly Income</h3>
                              </div>
                              <p className="text-3xl font-bold text-green-600">
                                ₹{results?.monthlyIncome?.toLocaleString('en-IN')}
                              </p>
                              <p className="text-sm text-gray-600 mt-2">
                                Your target monthly income requirement
                              </p>
                            </div>

                            <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-6 rounded-xl border-l-4 border-yellow-600">
                              <div className="flex items-center gap-3 mb-3">
                                <Calculator className="w-8 h-8 text-yellow-600" />
                                <h3 className="text-xl font-bold text-gray-800">Yearly Income Required</h3>
                              </div>
                              <p className="text-3xl font-bold text-yellow-600">
                                ₹{results?.yearlySwp?.toLocaleString('en-IN')}
                              </p>
                              <p className="text-sm text-gray-600 mt-2">
                                Annual income from SWP
                              </p>
                            </div>
                          </>
                        )}
                        
                        {/* Tax Disclaimer */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <strong>Note:</strong> This calculation does not account for Capital Gains Tax.{' '}
                            <a 
                              href="https://wa.me/918217589700?text=I%20need%20help%20with%20Tax%20Efficient%20SWP"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline font-semibold"
                            >
                              Contact us to structure a Tax-Efficient Withdrawal Plan
                            </a>
                            .
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {showFullResults && (
                <Card className="shadow-xl border-0">
                  <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
                    <CardTitle className="text-2xl md:text-3xl text-center">
                      Wealth Preservation Strategy Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-6">
                      <h4 className="font-bold text-gray-800 mb-4">Your Wealth Preservation Strategy:</h4>
                      <div className="space-y-3 text-gray-700">
                        <div className="flex justify-between">
                          <span>Calculation Mode:</span>
                          <span className="font-semibold">
                            {calculationMode === 'corpus-to-income' ? 'Corpus → Income' : 'Income → Corpus'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>SWP Rate:</span>
                          <span className="font-semibold">{results?.swpPercentage}% per annum</span>
                        </div>
                        {calculationMode === 'corpus-to-income' ? (
                          <>
                            <div className="flex justify-between">
                              <span>Available Corpus:</span>
                              <span className="font-semibold">₹{results?.corpusAmount?.toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Monthly Income:</span>
                              <span className="font-semibold">₹{results?.monthlySwp?.toLocaleString('en-IN')}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <span>Required Monthly Income:</span>
                              <span className="font-semibold">₹{results?.monthlyIncome?.toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Corpus Needed:</span>
                              <span className="font-semibold">₹{results?.corpusRequired?.toLocaleString('en-IN')}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span>Annual Income:</span>
                          <span className="font-semibold">₹{results?.yearlySwp?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Risk Assessment:</span>
                          <span className="font-semibold text-green-600">
                            {results?.swpPercentage <= 4 ? 'Excellent' : 
                             results?.swpPercentage <= 6 ? 'Good' : 'Risky'}
                          </span>
                        </div>
                      </div>
                      
                      {results?.swpPercentage <= 6 && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-700">
                            <strong>✓ Sustainable Strategy:</strong> Your withdrawal rate allows the remaining corpus to potentially grow with market returns while providing regular income.
                          </p>
                        </div>
                      )}
                      
                      {results?.swpPercentage > 6 && (
                        <div className="mt-4 p-3 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-700">
                            <strong>⚠ High Risk:</strong> Consider reducing your withdrawal rate to preserve capital during market downturns.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Lead Capture Modal */}
        <LeadCaptureModal
          isOpen={isLeadModalOpen}
          onClose={() => setIsLeadModalOpen(false)}
          onSubmit={handleLeadSubmit}
          toolName="Wealth Preservation Planner"
          isSubmitting={isSubmittingLead}
        />

        {/* SEBI Compliance Disclaimer */}
        <div className="mt-12 pt-8 border-t border-gray-300 max-w-8xl mx-auto">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">
              Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SWPPlanner;